// Vault Status
export enum VaultStatus {
  PENDING = 'pending',
  PENDING_USER_ACTIVATION = 'pending_user_activation',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  CLOSURE_INITIATED = 'closure_initiated',
  CLOSED = 'closed',
}

// Document Types
export enum DocumentType {
  PAYSLIP = 'payslip',
  DSN = 'dsn',
  EDITION = 'edition',
  CONTRACT = 'contract',
  OTHER = 'other',
}

// Permission Roles (Simplified - Zero-Knowledge model)
export enum PermissionRole {
  OWNER = 'owner', // Propriétaire unique (employé - accès lecture/téléchargement)
  CREATOR = 'creator', // Créateur (gestionnaire - config sync + historique, PAS d'accès contenu)
  ADMIN = 'admin', // @deprecated - Administrateur (gestion complète)
  CONTRIBUTOR = 'contributor', // @deprecated - Contributeur (lecture + upload)
  VIEWER = 'viewer', // @deprecated - Lecture seule
}

// Invitation Status
export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  REVOKED = 'revoked',
}

// Audit Actions
export enum AuditAction {
  VAULT_CREATED = 'vault_created',
  VAULT_ACTIVATED = 'vault_activated',
  VAULT_INITIALIZED = 'vault_initialized',
  VAULT_UNLOCKED = 'vault_unlocked',
  DOCUMENT_UPLOADED = 'document_uploaded',
  DOCUMENT_DOWNLOADED = 'document_downloaded',
  DOCUMENT_VIEWED = 'document_viewed',
  DOCUMENT_DELETED = 'document_deleted',
  DOCUMENT_SHARED = 'document_shared',
  PERMISSION_GRANTED = 'permission_granted',
  PERMISSION_REVOKED = 'permission_revoked',
  PERMISSION_MODIFIED = 'permission_modified',
  INVITATION_SENT = 'invitation_sent',
  INVITATION_ACCEPTED = 'invitation_accepted',
  INVITATION_DECLINED = 'invitation_declined',
  INVITATION_EXPIRED = 'invitation_expired',
  INVITATION_CREATED = 'invitation_created',
  ACCESS_GRANTED = 'access_granted',
  ACCESS_DENIED = 'access_denied',
  LOGIN_SUCCESS = 'login_success',
  LOGIN_FAILED = 'login_failed',
  EXPORT_REQUESTED = 'export_requested',
  EXPORT_COMPLETED = 'export_completed',
  VAULT_SUSPENDED = 'vault_suspended',
  VAULT_CLOSURE_INITIATED = 'vault_closure_initiated',
  VAULT_CLOSED = 'vault_closed',
}

// Period
export interface Period {
  year: number;
  month: number;
}

// Encryption Parameters
export interface EncryptionParams {
  algorithm: string;
  keyDerivation: string;
  iterations: number;
}

// Encrypted Vault Key (stored on server)
export interface EncryptedVaultKeyData {
  encryptedKey: string; // Base64-encoded
  iv: string; // Base64-encoded initialization vector
  authTag: string; // Base64-encoded authentication tag
}

// Document Encryption Metadata
export interface DocumentEncryptionMetadata {
  iv: string; // Base64-encoded initialization vector
  authTag: string; // Base64-encoded authentication tag
  encryptionAlgorithm: string; // e.g., 'AES-256-GCM'
}

// Vault Entity Type
export enum VaultEntityType {
  FOLDER = 'folder', // Vault for a company/folder
  EMPLOYEE = 'employee', // Vault for an employee
}

// Vault Encryption Mode
export enum VaultEncryptionMode {
  ZERO_KNOWLEDGE = 'zero_knowledge', // Client-side encryption (employee vaults)
  SERVER_SIDE = 'server_side', // Server-side encryption (folder/company vaults)
}

// Granular permissions for FOLDER SERVER_SIDE vaults
export interface FolderVaultPermission {
  canViewDocuments: boolean;
  canReadContent: boolean;
  canUploadDocument: boolean;
  canSendToEmployeeVault: boolean;
  canViewEmployeeVaults: boolean;
  canDepositToEmployeeVault: boolean;
}

// Vault
export interface Vault {
  uuid: string;
  ownerId: string;
  name: string;
  description?: string;
  status: VaultStatus;
  entityType?: VaultEntityType | null; // Type of linked entity
  entityId?: string | null; // UUID of linked entity (folder or employee)
  encryptionMode?: VaultEncryptionMode; // Encryption mode (zero_knowledge or server_side)
  /** @deprecated Use entityType and entityId instead */
  folderId?: string;
  encryptionParams: EncryptionParams | null;
  vaultKeySalt: string | null; // Salt for client-side key derivation
  publicKey?: string | null;
  encryptedPrivateKey?: string | null;
  encryptedVaultKey?: EncryptedVaultKeyData | null; // Encrypted vault master key (needed for unlock)
  metadata?: {
    contactEmail?: string;
    [key: string]: unknown;
  } | null;
  closureInitiatedAt?: string;
  closureScheduledFor?: string;
  createdAt: string;
  updatedAt: string;
  accessType?: 'direct' | 'via_folder';
}

// Vault Document
export interface VaultDocument {
  uuid: string;
  vaultId: string;
  uploadedBy: string;
  type: DocumentType;
  originalFilename: string;
  encryptedFilename: string;
  mimeType: string;
  originalSize: number;
  encryptedSize: number;
  storageUrl: string;
  contentHash: string;
  encryptedDocumentKey: string; // Encrypted with master key
  iv: string; // For document content
  authTag: string; // For document content
  documentKeyIv?: string | null; // For encrypted document key (nullable for backward compatibility)
  documentKeyAuthTag?: string | null; // For encrypted document key (nullable for backward compatibility)
  encryptionMetadata?: DocumentEncryptionMetadata; // IV and auth tag for decryption
  period?: Period;
  employeeNumber?: string;
  employeeDateOfBirth?: string;
  retentionUntil?: string;
  timestampToken?: string;
  metadata?: Record<string, unknown>;
  // Suivi de transfert employeur -> employé
  transferredToEmployeeVault?: boolean;
  transferredAt?: string | null;
  transferredBy?: string | null;
  sourceFolderDocumentId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// User info for permission display
export interface PermissionUser {
  uuid: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

// Permission Filters (optional restrictions on capabilities)
export interface PermissionFilters {
  documentTypes?: string[]; // Restrict to specific document types
  folders?: string[]; // Restrict to specific folders (future use)
  dateRange?: {
    from?: string;
    to?: string;
  };
}

// Vault Permission (Simplified Model)
export interface VaultPermission {
  uuid: string;
  vaultId: string;
  userId: string;
  user?: PermissionUser;
  role: PermissionRole; // Capabilities determined by role, not individual flags
  filters?: PermissionFilters | null; // Optional restrictions
  employeeNumber?: string | null; // For employee-specific access
  // Granular permissions (FOLDER SERVER_SIDE vaults)
  canViewDocuments?: boolean;
  canReadContent?: boolean;
  canUploadDocument?: boolean;
  canSendToEmployeeVault?: boolean;
  canViewEmployeeVaults?: boolean;
  canDepositToEmployeeVault?: boolean;
  expiresAt?: string | null;
  lastAccessedAt?: string | null;
  status?: string; // active, suspended, revoked
  createdAt: string;
  updatedAt: string;
}

// Vault Invitation
export interface VaultInvitation {
  uuid: string;
  vaultId: string;
  invitedById: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  permissionRole: PermissionRole;
  message?: string | null;
  expiresAt: string;
  acceptedAt?: string | null;
  status: InvitationStatus;
  employeeNumber?: string | null;
  dateOfBirth?: string | null;
  permissions?: {
    canRead?: boolean;
    canDownload?: boolean;
    canUpload?: boolean;
    documentTypes?: string[];
  } | null;
  grantedPermissions?: FolderVaultPermission | null;
  vaultInitializationPending?: boolean;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Additional fields for display
  vault?: {
    uuid: string;
    name: string;
  };
  invitedBy?: {
    uuid: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

// Vault Audit Log
export interface VaultAuditLog {
  uuid: string;
  vaultId: string;
  userId: string;
  action: AuditAction;
  details?: string | Record<string, unknown>; // Action details (can be string or object)
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  previousHash?: string;
  hash: string; // Current hash for blockchain-style audit chain
  createdAt: string;
}

export interface VaultAuditLogParams {
  page?: number;
  limit?: number;
  action?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

// DTOs

export interface CreateVaultDto {
  name: string;
  description?: string;
  folderId?: string;
  contactEmail?: string;
}

export interface UpdateVaultDto {
  name?: string;
  description?: string;
  publicKey?: string;
  encryptedPrivateKey?: string;
}

export interface InitializeVaultKeyDto {
  vaultKeySalt: string; // Base64-encoded salt
  encryptedVaultKey: EncryptedVaultKeyData; // Encrypted with derived key
  encryptionParams: {
    algorithm: string;
    iterations: number;
    keyDerivation: string;
  };
  publicKey?: string; // PEM format
  encryptedPrivateKey?: string; // Encrypted with vault master key (PEM format inside)
}

export interface UnlockVaultDto {
  password: string; // Used client-side only (never sent to server)
}

export interface UploadDocumentDto {
  file: File | Blob; // Encrypted file blob
  metadata: {
    originalName: string;
    originalSize: number;
    mimeType: string;
    iv: string; // Base64-encoded (for document content)
    authTag: string; // Base64-encoded (for document content)
    encryptedDocumentKey: string; // Base64-encoded encrypted document key
    documentKeyIv: string; // Base64-encoded (for encrypted document key)
    documentKeyAuthTag: string; // Base64-encoded (for encrypted document key)
    encryptionAlgorithm: string;
  };
  type: DocumentType;
  period?: Period;
  employeeNumber?: string;
  employeeDateOfBirth?: string;
  folderId?: string;
}

export interface GrantPermissionDto {
  userId: string;
  role: PermissionRole;
  canRead?: boolean;
  canWrite?: boolean;
  canDelete?: boolean;
  canShare?: boolean;
  canUpload?: boolean;
  canDownload?: boolean;
  filters?: {
    documentTypes?: DocumentType[];
    startDate?: string;
    endDate?: string;
    employeeNumbers?: string[];
  };
}

export interface CreateInvitationDto {
  email: string;
  permissionRole: PermissionRole;
  message?: string;
  employeeNumber?: string;
  dateOfBirth?: string;
  expiresInDays?: number;
  grantedPermissions?: FolderVaultPermission;
}

export interface GrantFolderPermissionDto {
  userId: string;
  canViewDocuments: boolean;
  canReadContent: boolean;
  canUploadDocument: boolean;
  canSendToEmployeeVault: boolean;
  canViewEmployeeVaults: boolean;
  canDepositToEmployeeVault: boolean;
}

export interface UpdateFolderPermissionDto {
  canViewDocuments?: boolean;
  canReadContent?: boolean;
  canUploadDocument?: boolean;
  canSendToEmployeeVault?: boolean;
  canViewEmployeeVaults?: boolean;
  canDepositToEmployeeVault?: boolean;
}

export interface AcceptInvitationDto {
  token: string;
  password?: string;
}

export interface AcceptInvitationDto {
  token: string;
  password?: string;
}

export interface EmployeeVaultRequest {
  employeeId: string;
  email: string;
  createVault?: boolean;
}

export interface MassCreateVaultDto {
  folderId: string;
  employees: EmployeeVaultRequest[];
}

// Response wrappers
export interface VaultResponse {
  vault: Vault;
  vaultKey?: string; // Deprecated: no longer returned (client-side encryption)
}

export interface VaultEncryptionParamsResponse {
  vaultKeySalt: string; // Base64-encoded salt
  encryptedVaultKey: EncryptedVaultKeyData; // Encrypted vault key
  encryptionParams: EncryptionParams; // Algorithm, iterations, etc.
}

export interface DocumentListResponse {
  documents: VaultDocument[];
  total: number;
  page: number;
  limit: number;
}

export interface AuditLogListResponse {
  logs: VaultAuditLog[];
  total: number;
  page: number;
  limit: number;
  chainValid: boolean;
}

export interface DocumentDownloadResponse {
  encryptedBlob: Blob;
  metadata: {
    originalName: string;
    originalSize: number;
    mimeType: string;
    iv: string; // Base64-encoded
    authTag: string; // Base64-encoded
  };
}

// Statistics
export interface VaultStatistics {
  totalDocuments: number;
  totalSize: number;
  documentsByType: Record<DocumentType, number>;
  oldestDocument?: string;
  newestDocument?: string;
  totalPermissions?: number;
  lastActivity?: string;
}
